// Internet file share via Supabase Storage (S8) — supabase provider.
//
// Capability-gated: the supabase provider advertises internetFileShare = true,
// so the UI exposes a "share over the internet" affordance. Files upload to the
// public `glacier-files` bucket and the recipient downloads via a public URL —
// works across networks, not just same-LAN (Beam's P2P path). The resulting
// AttachmentRef is written into messages.attachment via sendFileMessage.
//
// Public bucket + getPublicUrl matches the app's "effectively public, no auth"
// posture and avoids URL expiry. Switch the bucket to private + createSignedUrl
// here if privacy is wanted later — isolated to this file.
import { getClient } from './client.js';
import { newMessageId } from '../shapes.js';

const BUCKET = import.meta.env?.VITE_SUPABASE_STORAGE_BUCKET || 'glacier-files';

export const capabilities = { internetFileShare: true };

// Make a filename safe for a storage object path: collapse anything outside a
// conservative allowlist to '_', strip leading/trailing separators, and cap
// length so a pathological name can't blow the key limit. The unique prefix
// (newMessageId) guarantees no collision even if two files sanitize equal.
function safeName(name) {
  const base = String(name || 'file')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '');
  const cleaned = base || 'file';
  return cleaned.length > 120 ? cleaned.slice(-120) : cleaned;
}

/**
 * Upload a file to cloud storage scoped to a room. Resolves to an
 * AttachmentRef suitable for sendFileMessage({ ..., attachment }). Path is
 * `${code}/${senderId}/${uuid}-${safeName}` so objects are namespaced by room
 * and sender and never collide.
 *
 * @param {{ code: string, file: File, senderId: string }} args
 * @returns {Promise<{kind:'file', url:string, storagePath:string, name:string, size:number, mime:string}>}
 */
export async function uploadFile({ code, file, senderId }) {
  const supabase = getClient();
  const path = `${code}/${senderId}/${newMessageId()}-${safeName(file.name)}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return {
    kind: 'file',
    url: data.publicUrl,
    storagePath: path,
    name: file.name,
    size: file.size,
    mime: file.type,
  };
}

/**
 * Resolve a public download URL for a stored object path. Synchronous for a
 * public bucket; a signed-URL backend would return a Promise instead.
 *
 * @param {string} path  bucket-relative storage path
 * @returns {string}
 */
export function getFileUrl(path) {
  return getClient().storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

/**
 * Best-effort delete of a stored object (cleanup / retention). Never throws.
 *
 * @param {string} path  bucket-relative storage path
 * @returns {Promise<void>}
 */
export async function deleteFile(path) {
  try {
    await getClient().storage.from(BUCKET).remove([path]);
  } catch {
    /* best-effort */
  }
}
